import { useState } from "react";

interface Props {
  isOpen: boolean;
  title?: string;
  subjectEmail?: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void> | void;
}

// Per spec, reject of a verification request requires a free-text
// reason that is sent back to the applicant via email (F.2 SMTP).
// The UI keeps the reason mandatory; the email delivery itself is
// best-effort on the backend until SMTP lands.
export const RejectReasonModal = ({ isOpen, title, subjectEmail, onClose, onSubmit }: Props) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const onConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setReason("");
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-1 text-lg font-semibold text-slate-900">
          {title || "Отклонить заявку"}
        </h3>
        {subjectEmail && (
          <p className="mb-4 text-sm text-slate-500">
            Заявитель: <span className="font-medium text-slate-700">{subjectEmail}</span>
          </p>
        )}
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Причина отказа *
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          placeholder="Например: документ нечитаем; данные БИН не совпадают; ..."
          required
        />
        <p className="mt-1 text-xs text-slate-500">
          Текст причины будет отправлен заявителю на email.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || !reason.trim()}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Отправка..." : "Отклонить заявку"}
          </button>
        </div>
      </div>
    </div>
  );
};
